import { alpha } from "@mui/material/styles"
import { dsColorNew } from "@palette"


// STOPGAP — Iris drawer skin over the agent widget's INTERNAL class names (widget 0.107.0, 2026-10-21).
//
// The widget (rentacar-pms-agent `widget.js`, mounted by `Layout/PmsAgentWidget.jsx`) cannot be themed:
// it only takes `data-accent` / `data-radius`, appends one `<style id="dsb-root">` at mount (after ours,
// so ties go to it), has no shadow DOM and no stability contract for its `.dsb-*` classes. Until it
// exposes a theme, we override colours, borders, radii, fonts and shadows from here so the drawer
// matches the app's mock (company/missions/CU-869fkdhjv/artifacts/mock-iris-panel-v1.html). Layout
// rules (sizes, flex, positions) are deliberately left to the widget.
//
// Specificity is the lever: every selector carries `body .dsb-panel …` so it beats the widget's own
// rule of the same shape; `!important` only where the widget uses it (font family, header colours).
// Classes the widget's team calls long-stable: .dsb-panel .dsb-head .dsb-msgs .dsb-foot .dsb-user
// .dsb-bot .dsb-opt. The rest (.dsb-drawer .dsb-grip .dsb-status .dsb-fb* .dsb-opts .dsb-sugg-chip)
// were verified in the live 0.107.0 stylesheet and may break first on a widget release.
//
// To delete: remove this file and the `...irisSkinGlobalStyles` line in `Theme/index.jsx`.

const FONT = 'Roboto, sans-serif'
const P = 'body .dsb-panel'

export const irisSkinGlobalStyles = {
	// the app's font (the widget pins its own with !important, so this needs it too)
	[`${P}, ${P} *`]: { fontFamily: `${FONT} !important` },

	// docked drawer: a 1 px line against the page instead of a floating shadow (the drawer pushes the page)
	[`${P}.dsb-drawer.dsb-right:not(.full)`]: { boxShadow: 'none', borderLeft: `1px solid ${dsColorNew.grayBorder}` },
	[`${P}.dsb-drawer .dsb-grip::before`]: { background: dsColorNew.grayBorder },
	[`${P}.dsb-drawer .dsb-grip:hover`]: { background: alpha(dsColorNew.appBlue, 0.25) },

	// header: the side menu's navy, so the drawer's head reads as one band with the app's
	// chrome (Curro, 2026-10-21); the name in white, icon buttons muted white → white on hover
	[`${P} .dsb-head`]: {
		background: dsColorNew.navy,
		borderBottom: `1px solid ${dsColorNew.navyRaised}`,
		color: `${dsColorNew.white} !important`,
	},
	[`${P} .dsb-head *`]: { color: `${dsColorNew.white} !important` },
	[`${P} .dsb-head button`]: {
		color: `${alpha(dsColorNew.white, 0.72)} !important`,
		borderRadius: '8px',
		'&:hover': { color: `${dsColorNew.white} !important`, background: `${dsColorNew.navyRaisedHover} !important` },
	},
	// the icons draw with currentColor: keep them on the button's colour, not the header's
	[`${P} .dsb-head button *`]: { color: `${alpha(dsColorNew.white, 0.72)} !important` },
	[`${P} .dsb-head button:hover *`]: { color: `${dsColorNew.white} !important` },

	// thread on the app's page grey
	[`${P} .dsb-msgs`]: { background: dsColorNew.contentBg },

	// Iris answers: white card, 1 px light border, 12 px radius (the tail towards the avatar stays)
	[`${P} .dsb-bot`]: {
		background: dsColorNew.white,
		color: dsColorNew.textDark,
		border: `1px solid ${dsColorNew.grayBorder}`,
		borderRadius: '12px 12px 12px 4px',
		boxShadow: `0 1px 2px ${alpha(dsColorNew.navy, 0.04)}`,
	},
	[`${P} .dsb-bot a`]: { color: dsColorNew.appBlue },

	// user bubbles: the app blue, white text
	[`${P} .dsb-user`]: {
		background: dsColorNew.appBlue,
		color: dsColorNew.white,
		borderRadius: '12px 12px 4px 12px',
		boxShadow: 'none',
	},

	// "thinking" line: grey, upright, the pulsing dot in the app blue
	[`${P} .dsb-status`]: { color: dsColorNew.textMuted, fontStyle: 'normal', fontSize: 13 },
	[`${P} .dsb-status::before`]: { background: dsColorNew.appBlue },

	// feedback thumbs under an answer: flat icon buttons over a dashed line, muted → blue
	[`${P} .dsb-fb`]: { borderTop: `1px dashed ${dsColorNew.grayBorder}` },
	[`${P} .dsb-fb-btn`]: {
		background: 'transparent',
		borderColor: 'transparent',
		borderRadius: '8px',
		color: dsColorNew.textSubtle,
		'&:hover': { background: dsColorNew.hoverBgLight, color: dsColorNew.appBlue },
	},
	[`${P} .dsb-fb-send`]: { background: dsColorNew.appBlue, '&:hover': { background: dsColorNew.appBlueHover } },

	// quick replies inside an answer: chips outlined in blue
	[`${P} .dsb-opts`]: { borderTop: `1px solid ${dsColorNew.grayBorder}` },
	[`${P} .dsb-opt`]: {
		background: dsColorNew.white,
		border: `1px solid ${dsColorNew.appBlue}`,
		color: dsColorNew.appBlue,
		borderRadius: '8px',
		'&:hover': { background: dsColorNew.pillActiveBg, color: dsColorNew.appBlue, borderColor: dsColorNew.appBlue },
	},
	// welcome suggestions: the app's filter chips
	[`${P} .dsb-sugg-chip`]: {
		background: dsColorNew.white,
		border: `1px solid ${dsColorNew.grayBorder}`,
		color: dsColorNew.textDark,
		borderRadius: '10px',
		'&:hover': { background: dsColorNew.hoverBgLight, borderColor: dsColorNew.appBlue, color: dsColorNew.appBlue },
	},

	// composer: the app's input and its blue send button
	[`${P} .dsb-foot`]: { borderTop: `1px solid ${dsColorNew.grayBorder}`, background: dsColorNew.white },
	[`${P} .dsb-foot textarea`]: {
		border: `1px solid ${dsColorNew.grayBorder}`,
		borderRadius: '10px',
		background: dsColorNew.filterInputBg,
		color: dsColorNew.textTitle,
		'&::placeholder': { color: dsColorNew.textSubtle },
		'&:focus': {
			borderColor: dsColorNew.appBlue,
			background: dsColorNew.white,
			boxShadow: `0 0 0 3px ${alpha(dsColorNew.appBlue, 0.15)}`,
		},
	},
	[`${P} .dsb-foot button`]: {
		background: dsColorNew.appBlue,
		borderRadius: '10px',
		'&:hover:not(:disabled)': { background: dsColorNew.appBlueHover },
	},
}
